/**
 * Command — the shadcn command family on `cmdk` (same engine `Combobox` uses).
 *
 * Unlike `Combobox` (a closed value-picker), this exposes the raw parts so a
 * consumer can compose its own surface — the storefront's search type-ahead
 * renders server-fetched product rows inside a `CommandList` with
 * `shouldFilter={false}`, keeping cmdk for highlight management, keyboard
 * navigation and listbox/option ARIA while the matching happens in Postgres.
 *
 * `CommandInput` is included for completeness but is optional: cmdk's key
 * handling lives on the root element, so any input nested inside `Command`
 * (e.g. a plain form field that must keep working without JS) drives the list.
 */
import * as React from "react";
import { Command as CommandPrimitive, useCommandState } from "cmdk";
import { Search } from "lucide-react";
import { cn } from "../lib/cn.js";

/**
 * cmdk's internal state, for composing an **external** input.
 *
 * `CommandInput` wires `aria-controls`/`aria-activedescendant` itself, but a
 * consumer that must keep a native input (e.g. a form that has to submit
 * without JavaScript) gets neither. `useCommandState((s) => s.selectedItemId)`
 * returns the highlighted option's id so that input can carry
 * `aria-activedescendant` — the accessibility gap PR #258 review M1 found.
 *
 * Must be called from a component **inside** the `Command` root.
 */
export { useCommandState };

/** Root. Pass `shouldFilter={false}` when the item set is already filtered. */
export const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(function Command({ className, ...props }, ref) {
  return (
    <CommandPrimitive
      ref={ref}
      className={cn("flex w-full flex-col overflow-hidden text-ink", className)}
      {...props}
    />
  );
});

/** Search field with the leading icon — the `Combobox` header, extracted. */
export const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(function CommandInput({ className, ...props }, ref) {
  return (
    <div className="flex items-center gap-2 border-b border-line px-3">
      <Search aria-hidden size={14} className="shrink-0 text-muted" />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          "h-9 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-placeholder",
          className,
        )}
        {...props}
      />
    </div>
  );
});

/** Scrollable results region (cmdk renders it as the listbox). */
export const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(function CommandList({ className, ...props }, ref) {
  return (
    <CommandPrimitive.List
      ref={ref}
      className={cn("max-h-80 overflow-y-auto overflow-x-hidden p-1", className)}
      {...props}
    />
  );
});

/** Shown when the list has no items (with `shouldFilter={false}`: no children). */
export const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(function CommandEmpty({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Empty
      ref={ref}
      className={cn("px-3 py-6 text-center text-sm text-muted", className)}
      {...props}
    />
  );
});

/** Labelled section — heading styling matches the `Combobox` groups. */
export const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(function CommandGroup({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Group
      ref={ref}
      className={cn(
        "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.14em] [&_[cmdk-group-heading]]:text-muted",
        className,
      )}
      {...props}
    />
  );
});

export const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(function CommandSeparator({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Separator
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-line", className)}
      {...props}
    />
  );
});

/**
 * One selectable row. cmdk sets `data-selected` and `aria-selected` on the
 * highlighted item — style the highlight off `data-[selected=true]` (or a
 * `[data-selected="true"]` rule when composing with plain CSS classes).
 */
export const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(function CommandItem({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm outline-none",
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        className,
      )}
      {...props}
    />
  );
});
